import { useQuery } from "@tanstack/react-query";
import keyBy from "lodash-es/keyBy";
import { QUERY_KEYS } from "../config";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { Dictionary } from "./types";

const STALE_TIME = 15 * 60 * 1000;

export type TokensByIds = Partial<{
  price: number | string;
  change: number | string;
  symbol: string;
  id: string;
  name: string;
  logo: string;
  company_name?: string;
}>;

interface UseTokensByIdsOptions {
  /** 资产ID列表 */
  ids: string[];
  /** 资产类型 */
  type: "crypto" | "stock";
  /** 是否启用查询 */
  enabled?: boolean;
  /** 缓存时间，默认15分钟 */
  staleTime?: number;
}

/**
 * 根据ID列表获取代币或股票详细信息的通用Hook
 *
 * @param options 配置选项
 * @returns 返回格式化的资产数据字典
 */
export const useTokensByIds = ({
  ids,
  type,
  enabled = true,
  staleTime = STALE_TIME,
}: UseTokensByIdsOptions) => {
  const { openDataApi } = useOspApiClientProvider();

  return useQuery({
    queryKey: [
      type === "crypto" ? QUERY_KEYS.TOKENS_BY_IDS : QUERY_KEYS.STOCKS_BY_IDS,
      ids,
    ],
    enabled: !!ids && !!openDataApi && ids.length > 0 && enabled,
    staleTime,
    queryFn: async (): Promise<Maybe<Partial<TokensByIds>[]>> => {
      if (type === "crypto") {
        const res = await openDataApi?.tokens.getTokens({
          limit: ids.length,
          ids,
          "enrich.enable": true,
          "enrich.with_market.enable": true,
        });
        const data = res?.data?.data;
        if (!data?.rows?.length) {
          return undefined;
        }
        return data.rows.map((item) => {
          return {
            ...item,
            price: item.market_data?.current_price
              ? item.market_data?.current_price
              : undefined,
            change: item.market_data?.price_change_percentage?.find(
              (item) => item.time_period === "24h",
            )?.percentage,
          };
        });
      }

      // Stock logic
      const res = await openDataApi?.stocks.getStocks({
        limit: ids.length,
        ids,
        "enrich.with_trading_data": true,
        "enrich.enable": true,
      } as any);
      const data = res?.data?.data;
      if (!data?.rows?.length) {
        return undefined;
      }
      return data.rows.map((item) => {
        const change =
          (Number(item.trading_data?.current_price) /
            Number(item.trading_data?.previous_close) -
            1) *
          100;
        return {
          ...item,
          price: Number(item.trading_data?.current_price).toFixed(2),
          change: change,
        };
      });
    },
    select(data): Dictionary<Partial<TokensByIds>> {
      if (!data?.length) {
        return {};
      }
      return keyBy(data, "id");
    },
  });
};
